import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { cn } from '@/libs/utils'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  Loader2Icon,
  EditIcon,
  CheckIcon,
  XIcon,
  PlusIcon,
} from 'lucide-react'
import {
  useTemplateServiceGetTemplateQuery,
  useTemplateServiceUpdateTemplateMutation,
  useTemplateServiceSubmitTemplateMutation,
  useTemplateServiceApproveTemplateMutation,
  useTemplateServiceRejectTemplateMutation,
} from 'zma-react-sdk/template-service'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'

// Predefined variables
const PREDEFINED_VARIABLES = [
  'customer_name',
  'order_id',
  'amount',
  'delivery_date',
  'store_name',
  'phone',
]

const SAMPLE_VALUES: Record<string, string> = {
  customer_name: 'Nguyen Van A',
  order_id: 'ORD-2025-1125',
  amount: '150,000',
  delivery_date: '25/11/2025',
  store_name: 'Zalo Shop HCM',
  phone: '0909123456',
  promo_code: 'WELCOME10',
  address: '123 Nguyen Hue, Q1, TP.HCM',
  product_name: 'iPhone 15 Pro Max',
  quantity: '2',
  discount: '10%',
  total: '50,000,000',
  tracking_number: 'VN123456789',
  payment_method: 'ZaloPay',
}

function getStatusBadgeColor(status?: string | null) {
  switch (status) {
    case 'approved':
      return 'bg-green-100 text-green-800'
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    case 'rejected':
      return 'bg-red-100 text-red-800'
    case 'archived':
      return 'bg-slate-100 text-slate-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function getStatusText(status?: string | null) {
  switch (status) {
    case 'approved':
      return 'Approved'
    case 'draft':
      return 'Draft'
    case 'pending':
      return 'Pending Review'
    case 'rejected':
      return 'Rejected'
    case 'archived':
      return 'Archived'
    default:
      return status || 'Unknown'
  }
}

export function TemplateVersionDetailPage() {
  const { templateId, versionId } = useParams({ strict: false }) as {
    templateId: string
    versionId: string
  }
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()

  // Get edit mode from search params
  const searchParams = new URLSearchParams(window.location.search)
  const editFromUrl = searchParams.get('edit') === 'true'

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(editFromUrl)
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    variables: [] as string[],
  })
  const [success, setSuccess] = useState(false)
  const [showRejectDialog, setShowRejectDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [openInsertVar, setOpenInsertVar] = useState(false)
  const [newVarName, setNewVarName] = useState('')
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({})
  const [invalidVars, setInvalidVars] = useState<string[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { data, loading, error, refetch } = useTemplateServiceGetTemplateQuery({
    variables: { id: versionId },
    skip: !versionId,
  })

  const [updateTemplate, { loading: saving }] =
    useTemplateServiceUpdateTemplateMutation({
      onCompleted: () => {
        setSuccess(true)
        setIsEditMode(false)
        refetch()
        toast({
          title: t('common.success'),
          description: t('admin.templateUpdatedSuccessfully'),
        })
        setTimeout(() => setSuccess(false), 3000)
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  const [submitTemplate, { loading: submitting }] =
    useTemplateServiceSubmitTemplateMutation({
      onCompleted: () => {
        refetch()
        toast({
          title: t('common.success'),
          description: t('admin.templateSubmittedForApproval'),
        })
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  const [approveTemplate, { loading: approving }] =
    useTemplateServiceApproveTemplateMutation({
      onCompleted: () => {
        refetch()
        toast({
          title: t('common.success'),
          description: t('admin.templateApproved'),
        })
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  const [rejectTemplate, { loading: rejecting }] =
    useTemplateServiceRejectTemplateMutation({
      onCompleted: () => {
        setShowRejectDialog(false)
        setRejectReason('')
        refetch()
        toast({
          title: t('common.success'),
          description: t('admin.templateRejected'),
        })
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.message,
          variant: 'destructive',
        })
      },
    })

  // Extract variables from content
  const extractVariables = useCallback((content: string) => {
    const regex = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g
    const matches = content.match(regex) || []
    return [...new Set(matches.map((m) => m.replace(/{{\s*|\s*}}/g, '')))]
  }, [])

  // Validate variable names
  const validateVariables = useCallback((vars: string[]) => {
    return vars.filter((v) => !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v))
  }, [])

  // Initialize form when template loads
  useEffect(() => {
    if (data?.templateServiceGetTemplate) {
      const template = data.templateServiceGetTemplate
      setFormData({
        name: template.name || '',
        content: template.content || '',
        variables: (template.variables?.filter((v) => v) as string[]) || [],
      })
    }
  }, [data])

  // Auto-extract variables when content changes
  useEffect(() => {
    if (isEditMode) {
      const extracted = extractVariables(formData.content)
      setFormData((prev) => ({ ...prev, variables: extracted }))
      setInvalidVars(validateVariables(extracted))

      // Initialize preview values
      setPreviewValues((prev) => {
        const initialPreview = { ...prev }
        extracted.forEach((v) => {
          if (!(v in initialPreview)) {
            initialPreview[v] = SAMPLE_VALUES[v] || `sample_${v}`
          }
        })
        return initialPreview
      })
    }
  }, [formData.content, isEditMode, extractVariables, validateVariables])

  const handleSubmitForApproval = async () => {
    await submitTemplate({
      variables: {
        input: {
          id: versionId,
        },
      },
    })
  }

  const handleApprove = async () => {
    await approveTemplate({
      variables: {
        input: {
          id: versionId,
        },
      },
    })
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast({
        title: t('common.error'),
        description: t('admin.pleaseProvideRejectionReason'),
        variant: 'destructive',
      })
      return
    }

    await rejectTemplate({
      variables: {
        input: {
          id: versionId,
          rejectedReason: rejectReason,
        },
      },
    })
  }

  // Insert variable at cursor position
  const insertVariable = (varName: string) => {
    if (!textareaRef.current) return

    const start = textareaRef.current.selectionStart
    const end = textareaRef.current.selectionEnd
    const before = formData.content.substring(0, start)
    const after = formData.content.substring(end)
    const newValue = `${before}{{${varName}}}${after}`

    setFormData((prev) => ({ ...prev, content: newValue }))
    setOpenInsertVar(false)

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = start + varName.length + 4
        textareaRef.current.setSelectionRange(newPos, newPos)
        textareaRef.current.focus()
      }
    }, 0)
  }

  // Handle smart variable deletion
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      (e.key !== 'Backspace' && e.key !== 'Delete') ||
      textareaRef.current?.selectionStart !== textareaRef.current?.selectionEnd
    ) {
      return
    }

    const cursorPos = textareaRef.current!.selectionStart
    const varRegex = /{{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*}}/g
    let match
    let targetMatch: RegExpExecArray | null = null

    while ((match = varRegex.exec(formData.content)) !== null) {
      if (
        cursorPos >= match.index &&
        cursorPos <= match.index + match[0].length
      ) {
        targetMatch = match
        break
      }
    }

    if (!targetMatch) return

    e.preventDefault()
    const start = targetMatch.index
    const end = start + targetMatch[0].length
    let newStart = start
    let newEnd = end

    if (start > 0 && formData.content[start - 1] === ' ') newStart = start - 1
    if (end < formData.content.length && formData.content[end] === ' ')
      newEnd = end + 1

    const newValue =
      formData.content.substring(0, newStart) +
      formData.content.substring(newEnd)
    setFormData((prev) => ({ ...prev, content: newValue }))

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newStart, newStart)
      }
    }, 0)
  }

  // Update preview value
  const handlePreviewChange = (varName: string, value: string) => {
    setPreviewValues((prev) => ({ ...prev, [varName]: value }))
  }

  // Render preview
  const renderPreview = () => {
    let previewText = formData.content
    formData.variables.forEach((v) => {
      previewText = previewText.replace(
        new RegExp(`{{\\s*${v}\\s*}}`, 'g'),
        previewValues[v] || `{{${v}}}`
      )
    })
    return previewText
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    if (!formData.name || !formData.content) {
      toast({
        title: t('common.error'),
        description: t('admin.pleaseFillRequiredFields'),
        variant: 'destructive',
      })
      return
    }

    await updateTemplate({
      variables: {
        id: versionId,
        input: {
          name: formData.name,
          content: formData.content,
          variables: formData.variables,
        },
      },
    })
  }

  const handleCancel = () => {
    if (data?.templateServiceGetTemplate) {
      const template = data.templateServiceGetTemplate
      setFormData({
        name: template.name || '',
        content: template.content || '',
        variables: (template.variables?.filter((v) => v) as string[]) || [],
      })
    }
    setIsEditMode(false)
  }

  const hasChanges =
    data?.templateServiceGetTemplate &&
    (formData.name !== data.templateServiceGetTemplate.name ||
      formData.content !== data.templateServiceGetTemplate.content)

  if (loading) {
    return (
      <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
        <div className='mx-auto max-w-4xl space-y-4'>
          <div className='h-8 w-1/4 animate-pulse rounded bg-gray-200'></div>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className='h-24 animate-pulse rounded bg-gray-100'
              ></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !data?.templateServiceGetTemplate) {
    return (
      <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
        <Card className='mx-auto max-w-4xl'>
          <CardHeader>
            <CardTitle className='text-red-600'>{t('common.error')}</CardTitle>
            <CardDescription>
              {error?.message || t('admin.templateNotFound')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const template = data.templateServiceGetTemplate

  return (
    <div className='flex min-h-screen w-full flex-col bg-gradient-to-br from-emerald-50/30 to-blue-50/30 p-4 md:p-6'>
      <div className='mx-auto w-full max-w-6xl space-y-6'>
        {/* Header */}
        <header className='mb-6'>
          <div className='mb-4 flex flex-wrap items-center justify-between gap-4'>
            <div className='flex items-center gap-3'>
              <Button
                variant='ghost'
                size='icon'
                onClick={() =>
                  navigate({
                    to: '/admin/templates/$templateId',
                    params: { templateId } as any,
                  })
                }
              >
                <ArrowLeftIcon className='h-5 w-5' />
              </Button>
              <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500'>
                <span className='font-bold text-white'>T</span>
              </div>
              <div>
                <h1 className='text-2xl font-bold text-gray-900'>
                  {isEditMode ? t('admin.editTemplate') : template.name}
                </h1>
                <div className='mt-1 flex items-center gap-3'>
                  <code className='rounded bg-gray-100 px-2 py-1 text-sm'>
                    {template.templateId}
                  </code>
                  <span className='text-sm text-gray-500'>
                    v{template.version}
                  </span>
                </div>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <Badge
                className={cn(
                  'text-sm font-semibold',
                  getStatusBadgeColor(template.status)
                )}
              >
                {getStatusText(template.status)}
              </Badge>
              {!isEditMode &&
                template.status?.toLowerCase() !== 'pending_review' &&
                template.status?.toLowerCase() !== 'rejected' && (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => setIsEditMode(true)}
                  >
                    <EditIcon className='mr-2 h-4 w-4' />
                    {t('common.edit')}
                  </Button>
                )}
            </div>
          </div>
        </header>

        {/* Success Alert */}
        {success && (
          <Alert className='border-emerald-200 bg-emerald-50'>
            <CheckCircleIcon className='h-4 w-4 text-emerald-800' />
            <AlertDescription className='text-emerald-800'>
              {t('admin.templateUpdatedSuccessfully')}
            </AlertDescription>
          </Alert>
        )}

        {isEditMode ? (
          /* Edit Mode */
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            {/* Form Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.templateConfiguration')}</CardTitle>
                <CardDescription>
                  {t('admin.editTemplateDetailsBelow')}
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='name'>{t('admin.name')} *</Label>
                  <Input
                    id='name'
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={t('admin.templateName')}
                  />
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <Label htmlFor='content'>{t('admin.content')} *</Label>
                    <Popover
                      open={openInsertVar}
                      onOpenChange={setOpenInsertVar}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          size='sm'
                          className='h-8 gap-1 rounded-md border-dashed'
                        >
                          <PlusIcon className='h-3.5 w-3.5' />
                          <span>{t('admin.insertVariable')}</span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className='w-[300px] p-0' align='end'>
                        <Command>
                          <CommandInput
                            placeholder={t('admin.searchVariables')}
                          />
                          <CommandList className='max-h-[300px]'>
                            <CommandEmpty>
                              {t('admin.noVariablesFound')}
                            </CommandEmpty>
                            <CommandGroup heading={t('admin.createNew')}>
                              <div className='p-2'>
                                <div className='flex gap-2'>
                                  <Input
                                    value={newVarName}
                                    onChange={(e) =>
                                      setNewVarName(e.target.value)
                                    }
                                    placeholder={t('admin.variableName')}
                                    className='h-8 text-xs'
                                  />
                                  <Button
                                    size='sm'
                                    onClick={() => {
                                      if (
                                        newVarName.trim() &&
                                        /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(
                                          newVarName
                                        )
                                      ) {
                                        insertVariable(newVarName)
                                        setNewVarName('')
                                      }
                                    }}
                                  >
                                    {t('admin.add')}
                                  </Button>
                                </div>
                              </div>
                            </CommandGroup>
                          </CommandList>
                          <div className='sticky bottom-0 border-t bg-background'>
                            <CommandGroup
                              heading={t('admin.predefinedVariables')}
                            >
                              {PREDEFINED_VARIABLES.map((variable) => (
                                <CommandItem
                                  key={variable}
                                  onSelect={() => insertVariable(variable)}
                                >
                                  <span className='font-mono'>
                                    {'{{' + variable + '}}'}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </div>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Textarea
                    ref={textareaRef}
                    id='content'
                    value={formData.content}
                    onChange={(e) =>
                      handleInputChange('content', e.target.value)
                    }
                    onKeyDown={handleKeyDown}
                    placeholder={t('admin.enterYourMessageTemplate')}
                    className='min-h-[160px] font-mono'
                  />
                  <p className='text-xs text-muted-foreground'>
                    {t('admin.useDoubleBracesForVariables')}:{' '}
                    {'{{variable_name}}'}
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label>{t('admin.usedVariables')}</Label>
                  <div className='flex flex-wrap gap-2'>
                    {formData.variables.length === 0 ? (
                      <span className='text-sm italic text-muted-foreground'>
                        {t('admin.noVariablesDetected')}
                      </span>
                    ) : (
                      formData.variables.map((v) => (
                        <Badge
                          key={v}
                          variant={
                            invalidVars.includes(v)
                              ? 'destructive'
                              : 'secondary'
                          }
                          className='font-mono'
                        >
                          {v}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.preview')}</CardTitle>
                <CardDescription>
                  {t('admin.previewWithTestValues')}
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                {/* Test Values */}
                {formData.variables.length > 0 && (
                  <div className='space-y-3'>
                    <Label className='text-sm font-semibold'>
                      {t('admin.testVariables')}:
                    </Label>
                    <div className='space-y-2'>
                      {formData.variables.map((varName) => (
                        <div key={varName} className='flex items-center gap-2'>
                          <Badge
                            variant='outline'
                            className='min-w-[120px] justify-start font-mono text-xs'
                          >
                            {varName}
                          </Badge>
                          <Input
                            placeholder={`Value for ${varName}`}
                            value={previewValues[varName] || ''}
                            onChange={(e) =>
                              handlePreviewChange(varName, e.target.value)
                            }
                            className='h-8 text-sm'
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview */}
                <div>
                  <Label className='mb-2 block text-sm font-semibold'>
                    {t('admin.preview')}:
                  </Label>
                  <div className='min-h-[200px] rounded-lg bg-gray-50 p-4'>
                    <div className='whitespace-pre-wrap font-sans leading-relaxed text-gray-900'>
                      {renderPreview() || t('admin.yourMessageWillAppearHere')}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* View Mode */
          <>
            {/* Content and Variables */}
            <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
              {/* Content */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <span className='text-lg'>📝</span>
                    {t('admin.content')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className='whitespace-pre-wrap rounded-lg bg-gray-50 p-4 font-sans text-sm'>
                    {template.content}
                  </pre>
                </CardContent>
              </Card>

              {/* Variables */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <span className='text-lg'>💻</span>
                    {t('admin.variables')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {template.variables && template.variables.length > 0 ? (
                    <div className='flex flex-wrap gap-2'>
                      {template.variables.map((varName) =>
                        varName ? (
                          <Badge
                            key={varName}
                            className='bg-emerald-100 font-mono text-emerald-800'
                          >
                            {varName}
                          </Badge>
                        ) : null
                      )}
                    </div>
                  ) : (
                    <p className='italic text-gray-500'>
                      {t('admin.noVariablesDefined')}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Metadata */}
            <Card>
              <CardContent className='p-0'>
                <div className='grid grid-cols-1 divide-y md:grid-cols-2 md:divide-x md:divide-y-0'>
                  {/* Creation Info */}
                  <div className='p-5'>
                    <h3 className='mb-3 font-semibold text-gray-900'>
                      {t('admin.creationInfo')}
                    </h3>
                    <div className='space-y-2 text-sm'>
                      <div>
                        <span className='text-gray-500'>
                          {t('admin.createdBy')}:
                        </span>{' '}
                        <span className='font-medium'>
                          {template.createdBy || '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>
                          {t('admin.createdAt')}:
                        </span>{' '}
                        <span>
                          {template.createdAt
                            ? new Date(template.createdAt).toLocaleString(
                                'vi-VN'
                              )
                            : '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>
                          {t('admin.lastUpdated')}:
                        </span>{' '}
                        <span>
                          {template.updatedAt
                            ? new Date(template.updatedAt).toLocaleString(
                                'vi-VN'
                              )
                            : '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>
                          {t('admin.type')}:
                        </span>{' '}
                        <span className='font-medium'>{template.type}</span>
                      </div>
                    </div>
                  </div>

                  {/* Approval Info */}
                  <div className='p-5'>
                    <h3 className='mb-3 font-semibold text-gray-900'>
                      {t('admin.approvalInfo')}
                    </h3>
                    <div className='space-y-2 text-sm'>
                      <div>
                        <span className='text-gray-500'>
                          {t('admin.status')}:
                        </span>{' '}
                        <Badge
                          className={cn(
                            'ml-1',
                            getStatusBadgeColor(template.status)
                          )}
                        >
                          {getStatusText(template.status)}
                        </Badge>
                      </div>
                      {template.approvedAt && (
                        <div>
                          <span className='text-gray-500'>
                            {t('admin.approvedAt')}:
                          </span>{' '}
                          <span>
                            {new Date(template.approvedAt).toLocaleString(
                              'vi-VN'
                            )}
                          </span>
                        </div>
                      )}
                      {template.approvedBy && (
                        <div>
                          <span className='text-gray-500'>
                            {t('admin.approvedBy')}:
                          </span>{' '}
                          <span className='font-medium'>
                            {template.approvedBy}
                          </span>
                        </div>
                      )}
                      {template.rejectedReason && (
                        <div>
                          <span className='text-gray-500'>
                            {t('admin.rejectionReason')}:
                          </span>{' '}
                          <span className='text-red-600'>
                            {template.rejectedReason}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Action Buttons */}
        <div className='mt-6 flex flex-wrap justify-end gap-3'>
          {isEditMode ? (
            <>
              <Button variant='outline' onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={cn(
                  'gap-2',
                  template.status === 'approved'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                )}
              >
                {saving ? (
                  <>
                    <Loader2Icon className='h-4 w-4 animate-spin' />
                    {t('common.saving')}...
                  </>
                ) : template.status === 'approved' ? (
                  <>{t('admin.createNewDraft')}</>
                ) : (
                  <>{t('common.saveChanges')}</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='outline'
                onClick={() =>
                  navigate({
                    to: '/admin/templates/$templateId',
                    params: { templateId } as any,
                  })
                }
              >
                <ArrowLeftIcon className='mr-2 h-4 w-4' />
                {t('admin.backToVersions')}
              </Button>

              {template.status === 'approved' && (
                <Button disabled className='bg-emerald-600'>
                  {t('admin.activeTemplate')}
                </Button>
              )}

              {template.status === 'draft' && (
                <Button
                  className='bg-blue-600 hover:bg-blue-700'
                  onClick={handleSubmitForApproval}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                      {t('common.submitting')}...
                    </>
                  ) : (
                    t('admin.submitForApproval')
                  )}
                </Button>
              )}

              {template.status === 'pending' && (
                <>
                  <Button
                    variant='destructive'
                    onClick={() => setShowRejectDialog(true)}
                    disabled={rejecting}
                  >
                    {rejecting ? (
                      <>
                        <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                        {t('common.rejecting')}...
                      </>
                    ) : (
                      <>
                        <XIcon className='mr-2 h-4 w-4' />
                        {t('admin.reject')}
                      </>
                    )}
                  </Button>
                  <Button
                    className='bg-green-600 hover:bg-green-700'
                    onClick={handleApprove}
                    disabled={approving}
                  >
                    {approving ? (
                      <>
                        <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                        {t('common.approving')}...
                      </>
                    ) : (
                      <>
                        <CheckIcon className='mr-2 h-4 w-4' />
                        {t('admin.approve')}
                      </>
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </div>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.rejectTemplate')}</DialogTitle>
              <DialogDescription>
                {t('admin.pleaseProvideReasonForRejection')}
              </DialogDescription>
            </DialogHeader>
            <div className='space-y-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor='reject-reason'>
                  {t('admin.rejectionReason')} *
                </Label>
                <Textarea
                  id='reject-reason'
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('admin.enterRejectionReason')}
                  className='min-h-[100px]'
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => {
                  setShowRejectDialog(false)
                  setRejectReason('')
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant='destructive'
                onClick={handleReject}
                disabled={rejecting || !rejectReason.trim()}
              >
                {rejecting ? (
                  <>
                    <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                    {t('common.rejecting')}...
                  </>
                ) : (
                  t('admin.reject')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
